import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Warning } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { buildCsvFilename, downloadCsv } from '@/utils/file'

import Filter from '../Filter/Filter'
import SearchField from '../FormLegacy/FormFieldsV2/SearchField'
import Icon from '../Icons'
import API from '../../api/api'

import NewClientForm from './NewClientForm'

const ClientsNavbar = ({
  buildingId,
  buildingName,
  search,
  showUserStatusPopup,
  refreshClientsTable,
  isSiteLoading,
  userRole,
  allClientsCount,
  clientsFilter,
  setSiteLoading,
  setAllClientsCount,
  setClientsFilter,
  setSearch,
  clientsFilterOptions,
  onUIToggle,
}) => {
  const { t } = useTranslation(['clients-legacy', 'clients'])
  const [addNewClientVisible, setAddNewClientVisible] = useState(false)
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0)

  useEffect(() => {
    if (!buildingId) return
    API.getClientsWithCount(0, 0, buildingId, 0, '', 'pending')
      .then((data) => {
        if (data) setPendingApprovalCount(data.count)
      })
      .catch(console.log)
  }, [buildingId])

  const downloadFullCsvList = () => {
    API.downloadClientsCsvList(buildingId)
      .then((data) => {
        downloadCsv({
          blob: data,
          filename: buildCsvFilename({
            building: buildingName || null,
            page: 'clients',
            date: new Date(),
          }),
        })
      })
      .catch(() => {
        // add catch error
      })
  }

  const renderPendingApproval = () =>
    pendingApprovalCount ? (
      <div className="pending-approval">
        <Warning size={16} />
        {t('clients-legacy:navbar.pendingReviews', { count: pendingApprovalCount })}
        <span
          onClick={() => {
            const pendingOption = clientsFilterOptions.find((option) => option.value === 'pending')
            if (!pendingOption || clientsFilter?.value === 'pending') return
            setSiteLoading(true)
            setClientsFilter(pendingOption)
          }}
        >
          {t('clients-legacy:navbar.reviewLink')}
        </span>
      </div>
    ) : null

  return (
    <div className="ctp-clients-navbar">
      <div className="left-side">
        {['Administrator', 'PropertyManager'].includes(userRole) && buildingId !== 89 && (
          <div className="add-client-button">
            <button
              className="button button-primary"
              onClick={() => setAddNewClientVisible(true)}
              disabled={isSiteLoading}
            >
              {t('clients-legacy:navbar.addResidentButton')}
            </button>
          </div>
        )}
        {['Administrator', 'PropertyManager'].includes(userRole) && (
          <div className="download-all-button">
            <button
              className="button button-primary"
              onClick={downloadFullCsvList}
              disabled={isSiteLoading}
            >
              <Icon icon="download" />
              {t('clients-legacy:navbar.downloadAllButton')}
            </button>
          </div>
        )}
      </div>
      <div className="right-side">
        <ToggleSwitch
          falsyLabel={t('clients:list.designToggle.old')}
          truthyLabel={t('clients:list.designToggle.new')}
          checked={false}
          onChange={onUIToggle}
        />
        {renderPendingApproval()}
        <Filter
          placeholder={
            clientsFilter
              ? clientsFilterOptions.find((x) => x.id === clientsFilter.id).text
              : t('clients-legacy:navbar.filterPlaceholderFallback')
          }
          options={clientsFilterOptions}
          disabled={isSiteLoading}
          value={clientsFilter ? [clientsFilter] : []}
          name={'ClientsFilter'}
          additionalClass={'ctp-clients-navbar__filter'}
          onChange={(e) => {
            setSiteLoading(true)
            setClientsFilter(e.target.value)
          }}
          variant="borderless"
        />
        <SearchField
          placeholder={t('clients-legacy:navbar.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSiteLoading(true)
            setSearch(e.target.value)
          }}
        />
      </div>
      {addNewClientVisible && (
        <NewClientForm
          onClose={() => setAddNewClientVisible(false)}
          showUserStatusPopup={showUserStatusPopup}
          refreshClientsTable={() => {
            refreshClientsTable()
            setAllClientsCount(allClientsCount + 1)
          }}
        />
      )}
    </div>
  )
}

ClientsNavbar.propTypes = {
  buildingId: PropTypes.number,
  buildingName: PropTypes.string,
  allClientsCount: PropTypes.number,
  isSiteLoading: PropTypes.bool,
  userRole: PropTypes.string,
  search: PropTypes.string,
  // buildings: PropTypes.array,
  clientsFilter: PropTypes.object,
  showUserStatusPopup: PropTypes.func,
  refreshClientsTable: PropTypes.func,
  setSiteLoading: PropTypes.func,
  setAllClientsCount: PropTypes.func,
  setClientsFilter: PropTypes.func,
  clientsFilterOptions: PropTypes.array,
  setSearch: PropTypes.func,
  onUIToggle: PropTypes.func,
}

export default ClientsNavbar
